/**
 * FloatingWordsBackground
 *
 * 배경에 구름처럼 떠다니는 텍스트 컴포넌트
 * - Framer Motion 사용
 * - 각 텍스트가 랜덤 위치에서 좌↔우로 천천히 떠다님
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * 개별 떠다니는 텍스트 아이템
 */
const FloatingTextItem = ({ text, style, direction, duration, delay }) => {
  // 좌→우 또는 우→좌 움직임
  const xMove = direction === 'left' ? [-20, 20, -20] : [20, -20, 20];
  const yMove = [-5, 5, -5];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0, 0.5, 0.5, 0],
        x: xMove,
        y: yMove,
      }}
      transition={{
        duration: duration,
        delay: delay,
        repeat: Infinity,
        repeatDelay: Math.random() * 2,
        ease: 'easeInOut',
      }}
      style={{
        position: 'absolute',
        ...style,
      }}
    >
      <Typography
        sx={{
          fontFamily: '"source-han-serif-korean", "Noto Serif KR", serif',
          fontWeight: 500,
          color: 'text.disabled',
          opacity: 0.6,
          fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
          whiteSpace: 'nowrap',
          maxWidth: { xs: 200, sm: 280, md: 350 },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {text}
      </Typography>
    </motion.div>
  );
};

/**
 * FloatingWordsBackground
 *
 * @param {string[]} texts - 표시할 텍스트 배열
 * @param {number} count - 표시할 텍스트 개수 (기본: 10)
 * @param {object} sx - 추가 스타일
 */
const FloatingWordsBackground = ({
  texts = [],
  count = 8,
  sx = {},
}) => {
  // 랜덤으로 count개 선택 + 위치/애니메이션 설정
  const floatingItems = useMemo(() => {
    if (!texts || texts.length === 0) return [];

    const shuffled = [...texts].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, Math.min(count, texts.length));

    // 중앙을 피해 상단/하단에 분포 (y좌표 겹침 방지)
    // 상단 영역: 3% ~ 30%, 하단 영역: 70% ~ 97%
    const topZone = { start: 3, end: 30 };
    const bottomZone = { start: 70, end: 97 };

    // 절반은 상단, 절반은 하단에 배치
    const halfCount = Math.ceil(selected.length / 2);

    return selected.map((text, idx) => {
      let baseTop;

      if (idx < halfCount) {
        // 상단 영역에 균등 분배
        const zoneHeight = topZone.end - topZone.start;
        const rowHeight = zoneHeight / halfCount;
        baseTop = topZone.start + idx * rowHeight + rowHeight * 0.3;
      } else {
        // 하단 영역에 균등 분배
        const bottomIdx = idx - halfCount;
        const bottomCount = selected.length - halfCount;
        const zoneHeight = bottomZone.end - bottomZone.start;
        const rowHeight = zoneHeight / bottomCount;
        baseTop = bottomZone.start + bottomIdx * rowHeight + rowHeight * 0.3;
      }

      // x좌표: 랜덤 (좌우 여백 확보)
      const baseLeft = 5 + Math.random() * 80;

      return {
        text,
        style: {
          left: `${baseLeft}%`,
          top: `${baseTop}%`,
        },
        direction: idx % 2 === 0 ? 'left' : 'right',
        duration: 8 + Math.random() * 4, // 8~12초
        delay: idx * 0.4 + Math.random() * 0.5,
      };
    });
  }, [texts, count]);

  if (floatingItems.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        ...sx,
      }}
    >
      {floatingItems.map((item, idx) => (
        <FloatingTextItem
          key={idx}
          text={item.text}
          style={item.style}
          direction={item.direction}
          duration={item.duration}
          delay={item.delay}
        />
      ))}
    </Box>
  );
};

export default FloatingWordsBackground;
